import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select'; // Importación de react-select
import * as XLSX from 'xlsx'; // Para procesamiento de Excel
import Logo from './Logo';
import { fetchAuthSession } from 'aws-amplify/auth';
// Importaremos componentes de UI si es necesario, ej: Select, Checkbox, DatePicker
// import Select from 'react-select'; // Ejemplo si usamos react-select para multiselect

// --- Modal para Carga de Dispositivos desde Excel ---
function ExcelDeviceModal({
  isOpen,
  onClose,
  step,
  setStep,
  onFinalize,
  message,
  isError,
  bonusType
}) {
  const [uploadFile, setUploadFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [localHeaders, setLocalHeaders] = useState([]);
  const [localData, setLocalData] = useState([]);
  const [localColumnMapping, setLocalColumnMapping] = useState({});
  const [localValidationData, setLocalValidationData] = useState([]);
  const [localValidationErrors, setLocalValidationErrors] = useState([]);
  const [localHasValidationErrors, setLocalHasValidationErrors] = useState(false);
  const [excelWorkbook, setExcelWorkbook] = useState(null);
  const [excelSheets, setExcelSheets] = useState([]);
  const [selectedExcelSheet, setSelectedExcelSheet] = useState('');

  // Campos requeridos según el tipo de bono
  const requiredFields = bonusType === 'asignar_por_modelo' 
    ? ['name', 'bonusValue', 'bonusCode']
    : ['name', 'bonusValue'];

  const fieldLabels = {
    name: 'Nombre del dispositivo',
    bonusValue: 'Valor del bono',
    bonusCode: 'Código del bono'
  };

  // Función para formatear valores monetarios
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '' || value === '-') return '$0';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '$0';
    return new Intl.NumberFormat('es-CO', { 
      style: 'currency', 
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileUpload = (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetNames = workbook.SheetNames;
        
        // Auto-seleccionar la primera hoja
        const firstSheet = sheetNames[0];
        const worksheet = workbook.Sheets[firstSheet];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length === 0) {
          alert('El archivo Excel está vacío');
          return;
        }

        setExcelWorkbook(workbook);
        setExcelSheets(sheetNames);
        
        if (sheetNames.length === 1) {
          // Si solo hay una hoja, procesarla automáticamente
          processSelectedSheet(workbook, sheetNames[0]);
        } else {
          // Si hay múltiples hojas, permitir selección
          setStep('sheet-selection');
        }
        
        setUploadFile(file);
      } catch (error) {
        console.error('Error al procesar el archivo Excel:', error);
        alert('Error al procesar el archivo Excel. Asegúrate de que sea un archivo Excel válido.');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const processSelectedSheet = (workbook, sheetName) => {
    try {
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData.length === 0) {
        alert('La hoja seleccionada está vacía');
        return;
      }

      const headers = jsonData[0] || [];
      const dataRows = jsonData.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== ''));
      
      setLocalHeaders(headers);
      setLocalData(dataRows);
      setLocalColumnMapping({}); // Resetear mapeo
      setSelectedExcelSheet(sheetName);
      setStep('mapping');
    } catch (error) {
      console.error('Error al procesar la hoja:', error);
      alert('Error al procesar la hoja seleccionada');
    }
  };

  const handleSheetSelection = (sheetName) => {
    processSelectedSheet(excelWorkbook, sheetName);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content excel-modal">
        <div className="modal-header">
          <h3 className="modal-title">Cargar Dispositivos desde Excel</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {step === 'upload' && (
            <div className="upload-step">
              <p>Selecciona un archivo Excel con los datos de los dispositivos:</p>
              <ul style={{ marginBottom: '20px', color: '#666' }}>
                <li><strong>Nombre del dispositivo:</strong> Nombre del dispositivo (requerido)</li>
                <li>
                  <strong>Valor del bono:</strong>{' '}
                  {bonusType === 'asignar_por_modelo'
                    ? 'Valor numérico del bono, pueden ser cero, (si contiene letras o caracteres especiales se tomará como cero)'
                    : 'Valor numérico del bono (> 0) – requerido'}
                </li>
                {bonusType === 'asignar_por_modelo' && (
                  <li><strong>Código del bono:</strong> Código único del bono (requerido para tipo "Asignar por modelo")</li>
                )}
              </ul>
              
              <div 
                className={`upload-area ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="upload-content">
                  <div className="upload-icon">📊</div>
                  <p className="upload-text">
                    Arrastra tu archivo Excel aquí o{' '}
                    <label className="upload-link">
                      haz clic para seleccionar
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileInputChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </p>
                  <p className="upload-hint">Formatos soportados: .xlsx, .xls</p>
                </div>
              </div>
            </div>
          )}

          {step === 'sheet-selection' && (
            <div className="sheet-selection-step">
              <h4>Seleccionar Hoja</h4>
              <p>Tu archivo tiene múltiples hojas. Selecciona la que contiene los datos de los dispositivos:</p>
              
              <div style={{ display: 'grid', gap: '10px', marginTop: '20px' }}>
                {excelSheets.map(sheetName => (
                  <button
                    key={sheetName}
                    type="button"
                    onClick={() => handleSheetSelection(sheetName)}
                    style={{
                      padding: '12px 16px',
                      border: selectedExcelSheet === sheetName ? '2px solid #6366f1' : '1px solid #d0d0d0',
                      borderRadius: '8px',
                      backgroundColor: selectedExcelSheet === sheetName ? '#f0f9ff' : 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>📄</span>
                    <span>{sheetName}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 'mapping' && (
            <div className="mapping-step">
              <h4>Mapear Columnas</h4>
              <p>Indica qué columna de tu Excel corresponde a cada campo requerido:</p>
              
              {requiredFields.map(field => (
                <div key={field} className="mapping-row" style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    {fieldLabels[field]}:
                  </label>
                  <select
                    value={localColumnMapping[field] || ''}
                    onChange={(e) => setLocalColumnMapping({
                      ...localColumnMapping,
                      [field]: e.target.value
                    })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #d0d0d0',
                      borderRadius: '4px'
                    }}
                  >
                    <option value="">Seleccionar columna...</option>
                    {localHeaders.map((header, index) => (
                      <option key={index} value={index}>{header || `Columna ${index + 1}`}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          {step === 'validation' && (
            <div className="validation-step">
              <h4>Vista Previa y Validación</h4>
              <p>Revisa los datos que se van a importar:</p>
              
              {localValidationData.length > 0 && (
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                          Nombre
                        </th>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                          Valor Bono
                        </th>
                        {bonusType === 'asignar_por_modelo' && (
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                            Código Bono
                          </th>
                        )}
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                          Estado
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {localValidationData.slice(0, 10).map((item, index) => (
                        <tr key={index} style={{ backgroundColor: item.hasErrors ? '#ffe6e6' : 'white' }}>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            {item.name || '-'}
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            {formatCurrency(item.bonusValue)}
                          </td>
                          {bonusType === 'asignar_por_modelo' && (
                            <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                              {item.bonusCode || '-'}
                            </td>
                          )}
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            {item.hasErrors ? (
                              <span style={{ color: '#d32f2f', fontSize: '0.9rem' }}>❌ Error</span>
                            ) : (
                              <span style={{ color: '#2e7d32', fontSize: '0.9rem' }}>✅ Válido</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {localHasValidationErrors && (
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#ffe6e6', borderRadius: '4px' }}>
                  <strong>⚠️ Se encontraron errores en algunos registros</strong>
                  <p style={{ margin: '5px 0 0' }}>Solo se importarán los registros válidos.</p>
                </div>
              )}
            </div>
          )}

          {message && (
            <div className={`message ${isError ? 'message-error' : 'message-success'}`}>
              {message}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step === 'sheet-selection' && (
            <>
              <button className="btn btn-outline" onClick={() => setStep('upload')}>
                ← Volver
              </button>
            </>
          )}

          {step === 'mapping' && (
            <>
              <button className="btn btn-outline" onClick={() => setStep('upload')}>
                ← Volver
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  // Proceder a la validación
                  const isMappingComplete = requiredFields.every(field => localColumnMapping[field] !== undefined && localColumnMapping[field] !== '');
                  if (isMappingComplete) {
                    // Procesar datos para validación
                    const validatedData = localData.map((row, index) => {
                      const item = {
                        name: row[localColumnMapping.name] || '',
                        bonusValue: row[localColumnMapping.bonusValue] || '',
                        bonusCode: bonusType === 'asignar_por_modelo' ? (row[localColumnMapping.bonusCode] || '') : '',
                        hasErrors: false,
                        errors: []
                      };
                      
                      // Validaciones
                      if (!item.name || item.name.toString().trim() === '') {
                        item.hasErrors = true;
                        item.errors.push('Nombre requerido');
                      }
                      
                      // Validaciones específicas según tipo de bono
                      if (bonusType === 'asignar_por_modelo') {
                        // Para modelo: intentar convertir cualquier valor a número, si falla asignar 0
                        try {
                          if (item.bonusValue === '-' || item.bonusValue === '' || !item.bonusValue) {
                            item.bonusValue = 0;
                          } else {
                            // Intentar limpiar el valor y convertir a número
                            let cleanValue = item.bonusValue.toString().replace(/[^\d.-]/g, ''); // Quitar todo excepto dígitos, punto y guión
                            const numValue = parseFloat(cleanValue);
                            if (isNaN(numValue) || cleanValue === '' || cleanValue === '-') {
                              // Si no se puede convertir, asignar 0
                              item.bonusValue = 0;
                            } else {
                              // Si es numérico, usar ese valor
                              item.bonusValue = numValue;
                            }
                          }
                        } catch (error) {
                          // En caso de cualquier error, asignar 0
                          item.bonusValue = 0;
                        }
                      } else if (bonusType === 'asignar_catalogo_precios') {
                        // Para catálogo: debe ser numérico y mayor a cero (obligatorio para búsquedas)
                        if (!item.bonusValue || isNaN(parseFloat(item.bonusValue)) || parseFloat(item.bonusValue) <= 0) {
                          item.hasErrors = true;
                          item.errors.push('Valor de bono debe ser numérico y mayor a cero');
                        }
                      }
                      
                      if (bonusType === 'asignar_por_modelo' && (!item.bonusCode || item.bonusCode.toString().trim() === '')) {
                        item.hasErrors = true;
                        item.errors.push('Código de bono requerido');
                      }
                      
                      return item;
                    });
                    
                    setLocalValidationData(validatedData);
                    setLocalHasValidationErrors(validatedData.some(item => item.hasErrors));
                    setStep('validation');
                  } else {
                    alert('Por favor mapea todas las columnas requeridas');
                  }
                }}
              >
                Continuar →
              </button>
            </>
          )}
          
          {step === 'validation' && (
            <>
              <button className="btn btn-outline" onClick={() => setStep('mapping')}>
                ← Volver
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  // Finalizar e importar dispositivos válidos
                  const validDevices = localValidationData.filter(item => !item.hasErrors).map(item => ({
                    name: item.name,
                    bonusValue: item.bonusValue,
                    bonusCode: bonusType === 'asignar_por_modelo' ? item.bonusCode : ''
                  }));
                  onFinalize(validDevices);
                }}
              >
                Importar Dispositivos ({localValidationData.filter(item => !item.hasErrors).length})
              </button>
            </>
          )}
          
          {step === 'upload' && (
            <button className="btn btn-outline" onClick={onClose}>
              Cancelar
            </button>
          )}
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }
        
        .modal-content {
          background: white;
          border-radius: 8px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          max-width: 600px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }
        
        .excel-modal {
          max-width: 800px;
        }
        
        .modal-header {
          padding: 1.5rem 2rem 1rem;
          border-bottom: 1px solid #e0e0e0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .modal-title {
          margin: 0;
          color: #333;
        }
        
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          padding: 0.5rem;
        }
        
        .modal-body {
          padding: 2rem;
        }
        
        .modal-footer {
          padding: 1rem 2rem;
          border-top: 1px solid #e0e0e0;
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
        }
        
        .upload-area {
          border: 2px dashed #d0d0d0;
          border-radius: 8px;
          padding: 3rem 2rem;
          text-align: center;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        
        .upload-area:hover,
        .upload-area.drag-active {
          border-color: #6366f1;
          background-color: #f8f9ff;
        }
        
        .upload-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }
        
        .upload-text {
          margin: 0 0 0.5rem;
          font-size: 1.1rem;
        }
        
        .upload-link {
          color: #6366f1;
          cursor: pointer;
          text-decoration: underline;
        }
        
        .upload-hint {
          color: #666;
          font-size: 0.9rem;
          margin: 0;
        }
        
        .message {
          padding: 1rem;
          border-radius: 4px;
          margin-bottom: 1rem;
        }
        
        .message-success {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }
        
        .message-error {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }
      `}</style>
    </div>
  );
}

function CreateCampaign() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    applicableCompany: '',
    applyToAllBrands: false,
    applicableBrand: '',
    applicableCategory: '', // Nueva: categoría seleccionada
    applyToAllStores: true, // Siempre aplica a todas las tiendas
    applicableStores: [], // No se usa, siempre se envía 'ALL'
    bonusType: 'asignar_por_modelo', // Nueva: 'asignar_por_modelo' o 'asignar_catalogo_precios'
    applicableDevices: [{ name: '', bonusValue: '', bonusCode: '' }], // Array para dispositivos con código de bono
    bonusCollection: '', // Nueva: colección de bonos para catálogo por precios
    bonusCurrency: 'Pesos colombianos', // Valor por defecto
    title: '',
    description: ''
  });

  // Estado para advertencias de validación
  const [validationWarnings, setValidationWarnings] = useState({
    endDateInPast: false
  });

  // Estado para mensajes de éxito o error
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [brands, setBrands] = useState([]); // Estado para marcas
  const [stores, setStores] = useState([]); // Estado para tiendas (se cargan al elegir compañía)
  const [storeOptions, setStoreOptions] = useState([]); // Opciones formateadas para react-select
  const [categories, setCategories] = useState([]); // Estado para categorías
  const [bonusCollections, setBonusCollections] = useState([]); // Estado para colecciones de bonos
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingBrandsAndStores, setLoadingBrandsAndStores] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingBonusCollections, setLoadingBonusCollections] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Estados para modal de Excel
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelProcessingStep, setExcelProcessingStep] = useState('upload');
  const [excelWorkbook, setExcelWorkbook] = useState(null);
  const [excelSheets, setExcelSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [validationData, setValidationData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [excelMessage, setExcelMessage] = useState('');
  const [excelIsError, setExcelIsError] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isDevicesSectionCollapsed, setIsDevicesSectionCollapsed] = useState(false);

  // Función para obtener headers con autenticación Cognito
  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('No se pudo obtener el ID token');
      }
      
      return {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  };

  // Función para mostrar el mensaje de éxito y redirigir después de un tiempo
  const showSuccessAndRedirect = (message) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    
    // Redirigir después de 2.5 segundos
    setTimeout(() => {
      setShowSuccessMessage(false);
      navigate('/campaigns');
    }, 2500);
  };

  // --- Lógica para cargar datos --- 

  // Cargar compañías al montar el componente
  useEffect(() => {
    fetchCompanies();
    fetchBonusCollections(); // También cargar colecciones de bonos
  }, []);

  // Cargar marcas y tiendas cuando cambia la compañía seleccionada
  useEffect(() => {
    if (formData.applicableCompany) {
      fetchBrandsAndStores(formData.applicableCompany);
    } else {
      setBrands([]);
      setStores([]);
      setStoreOptions([]);
    }
  }, [formData.applicableCompany]);

  // Convertir el array de tiendas a formato para react-select
  useEffect(() => {
    const options = stores.map(store => ({
      value: store,
      label: store
    }));
    setStoreOptions(options);
  }, [stores]);

  // Cargar categorías cuando cambia la compañía y marca seleccionada
  useEffect(() => {
    if (formData.applicableCompany && formData.applicableBrand) {
      fetchBrandCategories(formData.applicableCompany, formData.applicableBrand);
    } else {
      setCategories([]);
    }
  }, [formData.applicableCompany, formData.applicableBrand]);

  // Validación de la fecha de finalización cuando cambia
  useEffect(() => {
    if (formData.endDate) {
      const today = new Date();
      // Convertir a formato YYYY-MM-DD para comparar solo la fecha, sin hora
      const todayString = today.toISOString().split('T')[0];
      const endDateString = formData.endDate; // Ya está en formato YYYY-MM-DD
      
      // Ahora comparamos solo las fechas como strings en formato YYYY-MM-DD
      // Solo será "en el pasado" si es estrictamente menor que la fecha de hoy
      setValidationWarnings(prev => ({
        ...prev,
        endDateInPast: endDateString < todayString
      }));
    } else {
      setValidationWarnings(prev => ({
        ...prev,
        endDateInPast: false
      }));
    }
  }, [formData.endDate]);

  // Función para obtener las compañías
  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
              const headers = await getAuthHeaders();
        const response = await axios.get(
          'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
          {
            headers,
            params: { info_type: 'companies' , tenant_id: localStorage.getItem('tenant_id') || ''}
          }
        );
      if (response.status === 200 && response.data.companies) {
        setCompanies(response.data.companies);
      } else {
        setError('No se pudieron cargar las compañías.');
      }
    } catch (error) {
      console.error('Error al obtener las compañías:', error);
      setError('Error al cargar las compañías.');
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Función para obtener marcas y tiendas en una sola llamada
  const fetchBrandsAndStores = async (companyName) => {
    if (!companyName) {
      setBrands([]);
      setStores([]);
      setStoreOptions([]);
      return;
    }

    setLoadingBrandsAndStores(true);
    try {
              const headers = await getAuthHeaders();
        const response = await axios.get(
          'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
          {
            headers,
            params: {
              info_type: 'create_user_post_company',
              company_name: companyName,
              tenant_id: localStorage.getItem('tenant_id') || ''
            }
          }
        );

      if (response.status === 200) {
        // Actualizar marcas
        if (response.data.brands) {
          setBrands(response.data.brands);
        } else {
          setBrands([]);
        }
        
        // Actualizar tiendas
        if (response.data.stores) {
          setStores(response.data.stores);
          // Las opciones se actualizarán a través del useEffect
        } else {
          setStores([]);
          setStoreOptions([]);
        }
      } else {
         setError('No se pudieron cargar las marcas y tiendas para la compañía seleccionada.');
      }
    } catch (error) {
      console.error('Error al obtener marcas y tiendas:', error);
      setError('Error al cargar las marcas y tiendas.');
      setBrands([]);
      setStores([]);
      setStoreOptions([]);
    } finally {
      setLoadingBrandsAndStores(false);
    }
  };

  // Función para obtener categorías de un retailer-marca específico
  const fetchBrandCategories = async (retailer, brand) => {
    if (!retailer || !brand) {
      setCategories([]);
      return;
    }

    setLoadingCategories(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/categories',
        {
          headers,
          params: {
            retailer,
            brand,
            search_type: 'categoriesToSell' // Parámetro correcto para categorías de venta
          }
        }
      );

      if (response.status === 200 && response.data) {
        setCategories(response.data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error al obtener categorías:', error);
      setError('Error al cargar las categorías.');
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Función para obtener colecciones de bonos disponibles
  const fetchBonusCollections = async () => {
    setLoadingBonusCollections(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonuses_codes',
        { 
          headers,
          params: { mode: 'all_collections' }
        }
      );
      
      const collectionsData = response.data.collections;
      console.log('🎁 [CreateCampaign] Collections data received:', collectionsData);
      
      // Transformar el objeto en array si es necesario
      let collectionsArray = [];
      if (Array.isArray(collectionsData)) {
        collectionsArray = collectionsData;
      } else if (collectionsData && typeof collectionsData === 'object') {
        collectionsArray = Object.keys(collectionsData).map(collectionId => ({
          id: collectionId,
          name: collectionsData[collectionId].collection_name
        }));
      }
      
      // Ordenar las colecciones por nombre en orden ascendente
      collectionsArray.sort((a, b) => {
        return a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
      });
      
      setBonusCollections(collectionsArray);
    } catch (error) {
      console.error('Error al obtener colecciones de bonos:', error);
      setError('Error al cargar las colecciones de bonos.');
      setBonusCollections([]);
    } finally {
      setLoadingBonusCollections(false);
    }
  };

  // Función para limpiar datos de texto
  const cleanTextData = (text) => {
    if (!text && text !== 0) return ''; // Manejar null, undefined, pero permitir 0
    const textString = String(text); // Convertir cualquier valor a string
    return textString
      .replace(/\r?\n|\r/g, ' ') // Quitar saltos de línea
      .replace(/\s{2,}/g, ' ')   // Reemplazar múltiples espacios por uno solo
      .trim();                   // Quitar espacios al inicio y final
  };

  // Función para ordenar dispositivos alfabéticamente por nombre
  const sortDevicesByName = (devices) => {
    return [...devices].sort((a, b) => 
      cleanTextData(a.name).toLowerCase().localeCompare(cleanTextData(b.name).toLowerCase(), 'es', { sensitivity: 'base' })
    );
  };

  // --- Handlers para cambios en el formulario --- 
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Si cambia la compañía, cargar marcas y tiendas
    if (name === 'applicableCompany' && value !== formData.applicableCompany) {
      fetchBrandsAndStores(value);
      // Resetear marca, categoría y tienda
      setFormData(prev => ({
        ...prev,
        [name]: value,
        applicableBrand: '',
        applicableCategory: '',
        applicableStores: []
      }));
    } else if (name === 'applicableBrand' && value !== formData.applicableBrand) {
      // Si cambia la marca, resetear categoría
      setFormData(prev => ({
        ...prev,
        [name]: value,
        applicableCategory: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Lógica adicional para checkboxes que deshabilitan/habilitan campos
    if (name === 'applyToAllBrands') {
      setFormData(prev => ({ ...prev, applicableBrand: '', applicableCategory: '' })); // Limpiar selección si aplica a todas
    }
    if (name === 'applyToAllStores') {
      setFormData(prev => ({ ...prev, applicableStores: [] })); // Limpiar selección si aplica a todas
    }
  };

  // Handler específico para react-select (tiendas)
  const handleStoreSelectChange = (selectedOptions) => {
    setFormData(prev => ({
      ...prev,
      applicableStores: selectedOptions ? selectedOptions.map(option => option.value) : []
    }));
  };

  const handleDeviceChange = (index, field, value) => {
    const newDevices = [...formData.applicableDevices];
    
    // Aplicar limpieza y validaciones según el campo
    if (field === 'bonusValue') {
      const numericValue = value.replace(/[^\d]/g, ''); // Quitar no dígitos
      const formattedValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, "."); // Añadir puntos
      newDevices[index][field] = formattedValue;
    } else if (field === 'bonusCode') {
      // Limpiar y convertir a mayúsculas
      const cleanedCode = cleanTextData(value).toUpperCase();
      newDevices[index][field] = cleanedCode;
    } else if (field === 'name') {
      // Limpiar nombre del dispositivo
      const cleanedName = cleanTextData(value);
      newDevices[index][field] = cleanedName;
    } else {
      newDevices[index][field] = value;
    }
    
    setFormData(prev => ({ ...prev, applicableDevices: newDevices }));
  };

  const addDevice = () => {
    setFormData(prev => ({
      ...prev,
      applicableDevices: [...prev.applicableDevices, { name: '', bonusValue: '', bonusCode: '' }]
    }));
  };

  const removeDevice = (index) => {
    const newDevices = formData.applicableDevices.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, applicableDevices: newDevices }));
  };

  // --- Lógica de envío --- 
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validaciones básicas aquí...

    const payload = {
      tenant_id: localStorage.getItem('tenant_id') || '',
      user_name: localStorage.getItem('user_name') || '',
      user_id: localStorage.getItem('user_id') || '',
      start_date: formData.startDate,
      end_date: formData.endDate,
      applicable_company: formData.applicableCompany,
      // Determinar el valor correcto para applicable_brand
      applicable_brand: formData.applyToAllBrands ? 'ALL' : formData.applicableBrand,
      applicable_category: formData.applicableCategory, // Nueva: categoría
      // Siempre enviar que aplica para todas las tiendas
      applicable_stores: ['ALL'],
      bonus_type: formData.bonusType, // Nueva: tipo de bono
      // Formatear dispositivos según el tipo de bono
      applicable_devices: formData.applicableDevices.map(device => {
        const deviceObj = {
          name: device.name,
          bonus_amount: parseInt(String(device.bonusValue || 0).replace(/\./g, ''), 10) || 0
        };
        
        // Solo agregar código si es asignar por modelo
        if (formData.bonusType === 'asignar_por_modelo') {
          deviceObj.bonus_code = device.bonusCode || '';
        }
        
        return deviceObj;
      }),
      // Solo agregar colección de bonos si es catálogo por precios
      ...(formData.bonusType === 'asignar_catalogo_precios' && {
        bonus_collection_id: formData.bonusCollection,
        bonus_collection_name: bonusCollections.find(collection => collection.id === formData.bonusCollection)?.name || ''
      }),
      bonus_currency: formData.bonusCurrency,
      title: formData.title,
      description: formData.description,
    };

    //console.log("Enviando payload:", payload);

    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/campaigns',
        payload,
        { headers }
      );

      if (response.status === 200 || response.status === 201) { // Aceptar 200 o 201 como éxito
        //console.log('Campaña creada:', response.data);
        // Mostrar mensaje de éxito personalizado en lugar de alert
        showSuccessAndRedirect('¡Campaña creada exitosamente!');
      } else {
        setError(`Error al crear la campaña: Estado ${response.status}`);
      }
    } catch (error) {
      console.error('Error al crear campaña:', error);
      setError('Error al crear la campaña: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Estilos personalizados para react-select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#6366F1' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #6366F1' : null,
      '&:hover': {
        borderColor: state.isFocused ? '#6366F1' : '#9ca3af',
      },
      fontSize: '16px',
      padding: '2px 0',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 3, // Asegurar que el menú esté por encima de otros elementos
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#E5E7FF',
      borderRadius: '4px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#4F46E5',
      fontSize: '14px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#4F46E5',
      '&:hover': {
        backgroundColor: '#C7D2FE',
        color: '#312E81',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      color: '#6B7280',
    }),
  };

  // --- Renderizado del formulario --- 
  return (
    <div className="create-campaign-container">
      {/* Mensaje de éxito flotante */}
      {showSuccessMessage && (
        <div className="success-message-overlay">
          <div className="success-message-container" style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '20px 30px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            maxWidth: '90%',
            width: '400px',
            textAlign: 'center',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <div className="success-icon" style={{
              backgroundColor: '#10B981', // Color verde de success similar al del brand
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              color: 'white',
              fontSize: '32px',
              fontWeight: 'bold'
            }}>
              ✓
            </div>
            <h3 style={{
              margin: '0 0 8px',
              color: '#1F2937',
              fontSize: '18px',
              fontWeight: '600'
            }}>{successMessage}</h3>
            <p style={{ 
              margin: '0',
              color: '#6B7280',
              fontSize: '14px'
            }}>Serás redirigido en unos segundos...</p>
          </div>
        </div>
      )}

      <header className="header">
        <Logo 
          variant="horizontal" 
          size="medium" 
          onClick={() => navigate('/dashboard')} 
        />
        <div className="header-title">Crear Nueva Campaña</div>
        <div className="user-menu">
          <button className="btn btn-outline" onClick={() => navigate('/campaigns')}>Volver a Campañas</button>
        </div>
      </header>

      <div className="container">
        <div className="card animate-fade-in">
          <h2 className="card-title">Crear Nueva Campaña</h2>
          <form onSubmit={handleSubmit} className="campaign-form">
            
            {/* Fechas */} 
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate" className="form-label">Fecha de Inicio *</label>
                <input 
                  id="startDate"
                  name="startDate"
                  type="date" 
                  className="form-input"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  required 
                />
                 {/* Recomendación: Usar react-datepicker */} 
              </div>
              <div className="form-group">
                <label htmlFor="endDate" className="form-label">Fecha de Finalización *</label>
                <input 
                  id="endDate"
                  name="endDate"
                  type="date" 
                  className={`form-input ${validationWarnings.endDateInPast ? 'input-warning' : ''}`}
                  value={formData.endDate}
                  onChange={handleInputChange}
                  min={formData.startDate} // Evitar fecha final anterior a inicio
                  required 
                  style={validationWarnings.endDateInPast ? {backgroundColor: '#FFFBEB', borderColor: '#F59E0B'} : {}}
                />
                {validationWarnings.endDateInPast && (
                  <div className="warning-message" style={{
                    color: '#B45309', 
                    fontSize: '0.85rem',
                    marginTop: '5px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <span style={{marginRight: '5px'}}>⚠️</span>
                    La fecha de finalización es anterior al día de hoy
                  </div>
                )}
              </div>
            </div>

            {/* Compañía */} 
            <div className="form-group">
              <label htmlFor="applicableCompany" className="form-label">Compañía Aplicable *</label>
              <select
                id="applicableCompany"
                name="applicableCompany"
                className="form-select"
                value={formData.applicableCompany}
                onChange={handleInputChange}
                required
                disabled={loadingCompanies}
              >
                <option value="" disabled>Selecciona una compañía...</option>
                {loadingCompanies ? (
                  <option value="" disabled>Cargando...</option>
                ) : (
                  companies.map((company, index) => (
                    <option key={index} value={company}>{company}</option>
                  ))
                )}
              </select>
            </div>

            {/* Marca */} 
            <div className="form-group">
              <label className="form-label">Marca Aplicable *</label>
              {/* <div className="checkbox-group">
                 <input 
                   type="checkbox" 
                   id="applyToAllBrands"
                   name="applyToAllBrands"
                   checked={formData.applyToAllBrands}
                   onChange={handleInputChange} 
                 />
                 <label htmlFor="applyToAllBrands">Aplicar a todas las marcas</label>
              </div> */}
              <select
                id="applicableBrand"
                name="applicableBrand"
                className="form-select"
                value={formData.applicableBrand}
                onChange={handleInputChange}
                required
                disabled={loadingBrandsAndStores || brands.length === 0}
              >
                <option value="" disabled>Selecciona una marca...</option>
                {loadingBrandsAndStores ? (
                  <option value="" disabled>Cargando...</option>
                ) : brands.length === 0 && !loadingBrandsAndStores ? (
                  <option value="" disabled>No hay marcas disponibles</option>
                ) : (
                  brands.map((brand, index) => (
                    <option key={index} value={brand}>{brand}</option>
                  ))
                )}
              </select>
            </div>

            {/* Categoría */} 
            <div className="form-group">
              <label htmlFor="applicableCategory" className="form-label">Categoría *</label>
              <select
                id="applicableCategory"
                name="applicableCategory"
                className="form-select"
                value={formData.applicableCategory}
                onChange={handleInputChange}
                required
                disabled={loadingCategories || categories.length === 0 || !formData.applicableBrand}
              >
                <option value="" disabled>
                  {!formData.applicableBrand ? 'Primero selecciona una marca...' : 'Selecciona una categoría...'}
                </option>
                {loadingCategories ? (
                  <option value="" disabled>Cargando categorías...</option>
                ) : categories.length === 0 && !loadingCategories && formData.applicableBrand ? (
                  <option value="" disabled>No hay categorías disponibles</option>
                ) : (
                  categories.map((category, index) => (
                    <option key={index} value={category}>{category}</option>
                  ))
                )}
              </select>
              {!formData.applicableBrand && <small className="input-help-text">Primero selecciona una compañía y marca</small>}
            </div>

            {/* Tipo de Bono */}
            <div className="form-group">
              <label htmlFor="bonusType" className="form-label">
                Tipo de Bono *
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <span 
                    className="help-icon" 
                    title="Haz clic para más información"
                    onClick={() => setShowTooltip(!showTooltip)}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    style={{ 
                      marginLeft: '8px', 
                      cursor: 'pointer', 
                      color: '#6366f1',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      transition: 'color 0.2s ease'
                    }}
                  >
                    ℹ️
                  </span>
                  {showTooltip && (
                    <div style={{
                      position: 'absolute',
                      top: '25px',
                      left: '-200px',
                      backgroundColor: '#333',
                      color: 'white',
                      padding: '12px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      lineHeight: '1.4',
                      width: '320px',
                      zIndex: 1000,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      border: '1px solid #6366f1'
                    }}>
                      <strong>Asignar por modelo:</strong> Se debe asignar un bono específico por cada dispositivo ingresado.<br/><br/>
                      <strong>Asignar catálogo por precios:</strong> Se asigna un catálogo de bonos y se relacionará automáticamente por el precio de cada dispositivo.
                      <div style={{
                        position: 'absolute',
                        top: '-8px',
                        left: '210px',
                        width: '0',
                        height: '0',
                        borderLeft: '8px solid transparent',
                        borderRight: '8px solid transparent',
                        borderBottom: '8px solid #333'
                      }}></div>
                    </div>
                  )}
                </div>
              </label>
              <select
                id="bonusType"
                name="bonusType"
                className="form-select"
                value={formData.bonusType}
                onChange={handleInputChange}
                required
              >
                <option value="asignar_por_modelo">Asignar por modelo</option>
                <option value="asignar_catalogo_precios">Asignar catálogo por precios</option>
              </select>
            </div>

            {/* Sección de Tiendas oculta - Siempre aplica a todas las tiendas */}
            {false && (
              <div className="form-group">
                <label className="form-label">Tiendas Aplicables *</label>
                <div className="react-select-container">
                  <Select
                    isMulti
                    name="applicableStores"
                    options={storeOptions}
                    className="react-select-input"
                    classNamePrefix="react-select"
                    placeholder="Aplica a todas las tiendas"
                    value={[]}
                    isDisabled={true}
                    styles={customSelectStyles}
                  />
                </div>
                <small className="input-help-text">
                  Esta campaña se aplicará automáticamente a todas las tiendas de la compañía.
                </small>
              </div>
            )}

            {/* Dispositivos Aplicables */} 
            <div className="form-group">
              <div 
                className="form-label" 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '10px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #e9ecef'
                }}
                onClick={() => setIsDevicesSectionCollapsed(!isDevicesSectionCollapsed)}
              >
                <span>
                  Dispositivos y Bonos Aplicables * 
                  <span style={{ 
                    marginLeft: '8px', 
                    fontSize: '14px', 
                    color: '#6c757d', 
                    fontWeight: 'normal' 
                  }}>
                    ({formData.applicableDevices.length} dispositivos)
                  </span>
                </span>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s ease' }}>
                  {isDevicesSectionCollapsed ? '▶️' : '▼️'}
                </span>
              </div>
              
              {!isDevicesSectionCollapsed && (
                <div style={{ marginTop: '15px' }}>
                  {/* Headers de columnas */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: formData.bonusType === 'asignar_por_modelo' ? '2fr 1.5fr 1.5fr auto' : '2fr 1.5fr auto',
                    gap: '10px', 
                    marginBottom: '10px',
                    padding: '8px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    <div>Nombre del Dispositivo</div>
                    <div>Valor del Bono</div>
                    {formData.bonusType === 'asignar_por_modelo' && <div>Código del Bono</div>}
                    <div>Acciones</div>
                  </div>

                  {/* Lista de dispositivos */}
                  {formData.applicableDevices.map((device, index) => (
                    <div key={index} style={{
                      display: 'grid',
                      gridTemplateColumns: formData.bonusType === 'asignar_por_modelo' ? '2fr 1.5fr 1.5fr auto' : '2fr 1.5fr auto',
                      gap: '10px',
                      alignItems: 'center',
                      marginBottom: '10px',
                      padding: '12px',
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px'
                    }}>
                      <input
                        type="text"
                        placeholder={`Ej: iPhone 14 Pro`}
                        className="form-input"
                        value={device.name}
                        onChange={(e) => handleDeviceChange(index, 'name', e.target.value)}
                        required
                        style={{ margin: 0 }}
                      />
                      <input
                        type="text"
                        placeholder={`Ej: 500000`}
                        className="form-input"
                        value={device.bonusValue}
                        onChange={(e) => handleDeviceChange(index, 'bonusValue', e.target.value)}
                        required
                        inputMode="numeric"
                        style={{ margin: 0 }}
                      />
                      {formData.bonusType === 'asignar_por_modelo' && (
                        <input
                          type="text"
                          placeholder={`Ej: BONUS001`}
                          className="form-input"
                          value={device.bonusCode}
                          onChange={(e) => handleDeviceChange(index, 'bonusCode', e.target.value)}
                          required
                          style={{ margin: 0 }}
                        />
                      )}
                      {formData.applicableDevices.length > 1 && (
                        <button 
                          type="button" 
                          className="btn btn-danger btn-sm"
                          onClick={() => removeDevice(index)}
                          title="Eliminar Dispositivo"
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}

                  {/* Botones de acción */}
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '15px' }}>
                    <button 
                      type="button" 
                      className="btn btn-outline btn-sm add-device-btn"
                      onClick={addDevice}
                    >
                      + Agregar Dispositivo
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowExcelModal(true)}
                    >
                      📊 Cargar desde Excel
                    </button>
                    {formData.applicableDevices.length > 1 && (
                      <button 
                        type="button" 
                        className="btn btn-outline btn-sm"
                        onClick={() => {
                          const sortedDevices = sortDevicesByName(formData.applicableDevices);
                          setFormData(prev => ({
                            ...prev,
                            applicableDevices: sortedDevices
                          }));
                        }}
                        title="Ordenar dispositivos alfabéticamente por nombre"
                      >
                        🔤 Ordenar A-Z
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Colección de Bonos - Solo cuando es 'asignar_catalogo_precios' */}
            {formData.bonusType === 'asignar_catalogo_precios' && (
              <div className="form-group">
                <label htmlFor="bonusCollection" className="form-label">Colección de Bonos *</label>
                <select
                  id="bonusCollection"
                  name="bonusCollection"
                  className="form-select"
                  value={formData.bonusCollection}
                  onChange={handleInputChange}
                  required
                  disabled={loadingBonusCollections || bonusCollections.length === 0}
                >
                  <option value="" disabled>
                    {loadingBonusCollections ? 'Cargando colecciones...' : 'Selecciona una colección de bonos...'}
                  </option>
                  {bonusCollections.length === 0 && !loadingBonusCollections ? (
                    <option value="" disabled>No hay colecciones disponibles</option>
                  ) : (
                    bonusCollections.map((collection, index) => (
                      <option key={index} value={collection.id}>{collection.name}</option>
                    ))
                  )}
                </select>
                <small className="input-help-text">
                  La colección de bonos se relacionará automáticamente con los dispositivos según su precio
                </small>
              </div>
            )}
            
            {/* Moneda */} 
            <div className="form-group">
              <label htmlFor="bonusCurrency" className="form-label">Moneda del Bono *</label>
              <select
                id="bonusCurrency"
                name="bonusCurrency"
                className="form-select"
                value={formData.bonusCurrency}
                onChange={handleInputChange}
                required
              >
                {/* Podríamos cargar esto desde config o API si hubiera más opciones */}
                <option value="Pesos colombianos">Pesos Colombianos (COP)</option>
                <option value="Pesos chilenos">Pesos Chilenos (CLP)</option>
                <option value="Pesos mexicanos">Pesos Mexicanos (MXN)</option>
                {/* <option value="USD">Dólares Americanos (USD)</option> */}
              </select>
            </div>

             {/* Título */} 
            <div className="form-group">
              <label htmlFor="title" className="form-label">Título de la Campaña *</label>
              <input 
                id="title"
                name="title"
                type="text" 
                className="form-input"
                placeholder="Ej: Campaña día de la madre - Galaxy S25"
                maxLength={80}
                value={formData.title}
                onChange={handleInputChange}
                required 
              />
              <small className="input-help-text">Máximo 80 caracteres.</small>
            </div>

            {/* Descripción */} 
            <div className="form-group">
              <label htmlFor="description" className="form-label">Descripción de la Campaña *</label>
              <textarea
                id="description"
                name="description"
                className="form-input"
                placeholder="Ej: Recibe un bono adicional por la compra de un Galaxy S25 al entregar tu dispositivo usado"
                maxLength={1000}
                rows={4}
                value={formData.description}
                onChange={handleInputChange}
                required
              ></textarea>
              <small className="input-help-text">Máximo 1000 caracteres.</small>
            </div>

            {error && <div className="message message-error" role="alert">{error}</div>}

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-outline" 
                onClick={() => navigate('/campaigns')}
                disabled={isSubmitting}
              >
                Regresar
              </button>
              <button 
                type="submit" 
                className={`btn btn-primary ${isSubmitting ? 'btn-loading' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creando Campaña...' : 'Crear Campaña'}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Modal de Excel para Dispositivos */}
      {showExcelModal && (
        <ExcelDeviceModal
          isOpen={showExcelModal}
          onClose={() => {
            setShowExcelModal(false);
            setExcelProcessingStep('upload');
          }}
          step={excelProcessingStep}
          setStep={setExcelProcessingStep}
          onFinalize={(devices) => {
            // Limpiar datos y ordenar dispositivos del Excel
            const cleanedDevices = devices.map(device => ({
              name: cleanTextData(device.name),
              bonusValue: device.bonusValue,
              bonusCode: cleanTextData(device.bonusCode || '').toUpperCase()
            }));
            
            const sortedDevices = sortDevicesByName(cleanedDevices);
            
            // Reemplazar los dispositivos actuales con los del Excel (limpiados y ordenados)
            setFormData(prev => ({
              ...prev,
              applicableDevices: sortedDevices
            }));
            setShowExcelModal(false);
            setExcelProcessingStep('upload');
          }}
          message={excelMessage}
          isError={excelIsError}
          bonusType={formData.bonusType}
        />
      )}
    </div>
  );
}

export default CreateCampaign; 